/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-09-29
 */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@nebula-shared/data-access';
import { generarIdentificadorDescarga } from '@nebula-shared/util';
import {
  ButtonComponent,
  DialogFooterComponent,
  DialogHeaderDirective,
  DialogBodyDirective,
  IconComponent,
  CodeFieldComponent,
} from 'nebula-ui-kit';

/**
 * Describe una única opción de formato de exportación disponible en el modal.
 */
export interface ExportFormatOptions {
  /** Identificador único del formato de exportación, como "csv" o "xlsx". */
  value: string;
  /** Etiqueta legible que se muestra para la opción de formato. */
  label: string;
  /** Referencia de icono opcional utilizada por la interfaz de usuario para renderizar el elemento de formato. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any;
  /** Clave de traducción o texto asociado con la descripción del formato de exportación. */
  text: string;
}

/**
 * Carga útil (payload) emitida cuando el usuario confirma una operación de exportación.
 */
export interface ExportRequestEvents {
  /** Formato seleccionado para la exportación, por ejemplo "csv" o "xlsx". */
  format: string;
  /** Parámetros opcionales a incluir en la solicitud de exportación. */
  params?: Record<string, string | number | boolean | null | undefined>;
}

/**
 * Componente modal utilizado para seleccionar un formato de exportación y confirmar una solicitud de descarga.
 *
 * El componente expone Angular Signals e inputs para controlar los formatos de exportación disponibles,
 * la generación del nombre de archivo, el estado de carga y los parámetros de la solicitud.
 * Emite el formato seleccionado y la carga útil final de la exportación al componente padre.
 */
@Component({
  selector: 'app-export-modal',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    TranslatePipe,
    DialogHeaderDirective,
    DialogBodyDirective,
    DialogFooterComponent,
    IconComponent,
    CodeFieldComponent,
  ],
  templateUrl: './export-modal.component.html',
  styleUrls: ['./export-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportModalComponent {
  /** Etiqueta mostrada para la acción del modal o el título del botón. */
  label = input('Exportar');

  /** Formatos de exportación disponibles que se pueden seleccionar en el modal. */
  options = input<ExportFormatOptions[]>([
    {
      value: 'csv',
      label: 'CSV (.csv)',
      icon: 'FileSpreadsheet',
      text: 'COMMON.EXPORT.MESSAGE.CSV',
    },
    {
      value: 'xlsx',
      label: 'Excel (.xlsx)',
      icon: 'FileX',
      text: 'COMMON.EXPORT.MESSAGE.EXCEL',
    },
  ]);

  /** Deshabilita las acciones del modal cuando se establece en verdadero. */
  disabled = input(false);

  /** Muestra el estado de carga mientras se procesa la exportación. */
  loading = input(false);

  /** Número total de elementos representados por la exportación actual. */
  totalElements = input<number>(0);

  /** Nombre base utilizado para generar el nombre final del archivo descargado. */
  nombreArchivo = input<string>('');

  /** Nombre de archivo generado computado utilizando el nombre de origen y la marca de tiempo actual. */
  nombreCompuesto = computed<string>(() =>
    generarIdentificadorDescarga(this.nombreArchivo(), new Date()),
  );

  /** Parámetros enviados junto con la solicitud de exportación. */
  requestParams = input<Record<string, string | number | boolean | null | undefined> | undefined>(
    undefined,
  );

  /** Formato de exportación seleccionado actualmente o null cuando no hay ningún formato seleccionado. */
  protected readonly itemSelected = signal<string | null>(null);

  /** Emite el formato seleccionado cuando un usuario elige una opción de exportación. */
  exportSelected = output<string>();

  /** Emite la carga útil completa de la solicitud de exportación tras la confirmación. */
  exportRequested = output<ExportRequestEvents>();

  /** Emite cuando el modal se cierra o descarta. */
  closed = output();

  /** Almacena el formato activo actualmente seleccionado en el modal. */
  selectedFormat = signal('');

  /**
   * Crea el componente y sincroniza el formato seleccionado con las opciones disponibles.
   */
  constructor() {
    effect(() => {
      const list = this.options();
      const current = this.selectedFormat();
      if (!list.length) {
        this.selectedFormat.set('');
        return;
      }
      const exists = list.some((x) => x.value === current);
      if (!exists) {
        this.selectedFormat.set(list[0].value);
      }
    });
  }

  /**
   * Actualiza el formato seleccionado cuando el usuario lo cambia desde la interfaz de usuario.
   *
   * @param value - Nuevo valor de formato seleccionado por el usuario.
   */
  onFormatChange(value: string): void {
    this.selectedFormat.set(value);
  }

  /**
   * Cierra el modal sin enviar una solicitud de exportación.
   */
  cancel(): void {
    this.closed.emit();
    if (this.loading()) return;
  }

  /**
   * Confirma la exportación seleccionada y emite la carga útil de la solicitud para que la gestione el componente padre.
   */
  confirmDownload(): void {
    const value = this.selectedFormat().trim();
    if (!value) return;
    this.closed.emit();
    this.exportSelected.emit(value);
    this.exportRequested.emit({ format: value, params: this.requestParams() });
  }

  /**
   * Cierra el modal cuando el usuario presiona la tecla Escape y el componente no está en estado de carga.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.loading()) return;
    this.closed.emit();
  }
}
